import {Component, OnInit} from '@angular/core';
import {BehaviorSubject, forkJoin, Observable, of} from 'rxjs';
import {RealEstate} from '../../interfaces/real-estate';
import {FavoritesService} from '../../services/favorites/favorites.service';
import {RealEstateService} from '../../services/real-estate/real-estate.service';
import {initFlowbite} from 'flowbite';
import {catchError, map} from 'rxjs/operators';
import {AsyncPipe} from '@angular/common';
import {RealEstateCardComponent} from '../real-estate-card/real-estate-card.component';
import {Base64Helper} from '../../utils/base64-helper';

@Component({
  selector: 'app-favorites',
  imports: [
    AsyncPipe,
    RealEstateCardComponent
  ],
  templateUrl: './favorites.component.html',
  styleUrl: './favorites.component.css'
})
export class FavoritesComponent implements OnInit{
  favoriteRealEstates = new BehaviorSubject<RealEstate[]>([]);
  favoriteRealEstates$: Observable<RealEstate[]> = this.favoriteRealEstates.asObservable();
  loading = false;

  constructor(
    private favoritesService: FavoritesService,
    private realEstateService: RealEstateService
  ) {}

  ngOnInit() {
    // Subscribe to favorites and load real estates when favorites change
    this.favoritesService.favoritesObservable.subscribe(favoriteIds => {
      if (favoriteIds && favoriteIds.length > 0) {
        this.loadFavoriteRealEstates(favoriteIds);
      } else {
        this.favoriteRealEstates.next([]);
      }
    });

    // Initial load of favorites
    this.favoritesService.getFavorites();
  }

  loadFavoriteRealEstates(favoriteIds: string[]): void {
    this.loading = true;
    console.log(`Starting to load ${favoriteIds.length} favorite real estates`);

    // Handle the case when there are no favorite IDs
    if (favoriteIds.length === 0) {
      console.log('No favorite real estates to load');
      this.favoriteRealEstates.next([]);
      this.loading = false;
      return;
    }

    // Create an array of observables for each real estate request
    let realEstateObservable = favoriteIds.map((id, index) => {
      console.log(`[${index + 1}/${favoriteIds.length}] Starting request for real estate ID: ${id}`);
      return this.realEstateService.getRealEstate(Base64Helper.encode(id)).pipe(
        map(result => {
          console.log(`[${index + 1}/${favoriteIds.length}] Successfully fetched real estate ID: ${id}`);
          return result;
        }),
        catchError(error => {
          console.error(`[${index + 1}/${favoriteIds.length}] Error fetching real estate with ID ${id}:`, error);
          return of(null); // Return null for failed requests
        })
      );
    });

    console.log(`Created ${realEstateObservable.length} observables for ${favoriteIds.length} requests`);

    // Use forkJoin to wait for all requests to complete
    forkJoin(realEstateObservable).pipe(
      map(realEstates => {
        console.log(`All ${favoriteIds.length} requests completed. Results:`, realEstates);
        // Filter out any null values and cast to RealEstate array
        const filteredRealEstates = realEstates.filter(estate => estate !== null);
        console.log(`${filteredRealEstates.length} valid real estates found after filtering nulls`);
        return (filteredRealEstates as RealEstate[]);
      })
    ).subscribe({
      next: (realEstates) => {
        console.log(`Successfully loaded ${realEstates.length} favorite real estates`);
        // Update the favorites list with the loaded real estates
        this.favoriteRealEstates.next(realEstates);
        this.loading = false;
      },
      error: (error) => {
        // Handle any errors that occur while loading
        console.error('Error loading favorite real estates:', error);
        this.loading = false;
        // Even on error, update the favorites list with an empty array
        this.favoriteRealEstates.next([]);
      },
      complete: () => {
        console.log('ForkJoin operation completed');
      }
    });
  }

}
